// store/v3/catalog.ts — menu / settings catalog (SPEC §3 CatalogStoreApi)
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type {
  ChannelPriceMap, MenuGroup, Product, ProductAllowMap, ProductsPayload,
  SettingRecord, SettingsPayload, SoldoutMap, Zone,
} from '~/types/v3/entities'
import type { EntityVersions, Versioned } from '~/types/v3/datasource'
import type { CatalogStoreApi } from '~/types/v3/stores'
import { filterMenuByAllowlist, flattenItems } from '~/utils/v3/menuFilter'

/** Legacy `findValueByIdInList` — first settings record with this id. */
function settingValue(list: readonly SettingRecord[], settingId: string): unknown {
  return list.find((record) => record?.settingId === settingId)?.value
}

/** settings record `tables` = `[{ name, value }]` (legacy zone list). */
function normalizeZones(value: unknown): Zone[] {
  if (!Array.isArray(value)) return []
  const zones: Zone[] = []
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue
    const zone = raw as { name?: unknown; value?: unknown }
    zones.push({ name: String(zone.name ?? ''), value: Number(zone.value) || 0 })
  }
  return zones
}

export const useCatalogStore = defineStore('v3-catalog', () => {
  const settings = ref<SettingRecord[]>([])
  const products = ref<Product[]>([])
  const groups = ref<MenuGroup[]>([])
  const channelPrice = ref<ChannelPriceMap>({})
  const menuSet = ref<ProductAllowMap>({})
  const soldout = ref<SoldoutMap>({})
  const versions = ref<Partial<EntityVersions>>({})
  const hydrated = ref(false)
  const storeProfileName = ref('')

  const storeName = computed<string>(() => {
    const fromSettings = settingValue(settings.value, 'name')
    if (typeof fromSettings === 'string' && fromSettings) return fromSettings
    return storeProfileName.value || ''
  })

  const zones = computed<Zone[]>(() => normalizeZones(settingValue(settings.value, 'tables')))

  /** Allowlist-filtered menu (utils/v3/menuFilter). */
  const menuGroups = computed<MenuGroup[]>(() => filterMenuByAllowlist(groups.value, menuSet.value))

  /** Flat list of visible items — used by search. */
  const allMenuItems = computed<Product[]>(() => flattenItems(menuGroups.value))

  const productById = computed<Map<string, Product>>(() => {
    const map = new Map<string, Product>()
    for (const product of products.value) {
      if (product?.productId) map.set(product.productId, product)
    }
    // Group items win nothing: only fill ids the flat list did not carry.
    for (const group of groups.value) {
      for (const item of group?.items ?? []) {
        if (item?.productId && !map.has(item.productId)) map.set(item.productId, item)
      }
    }
    return map
  })

  /** `channelPrice[channelId]?.[productId] ?? product.price` — 0 is a valid channel price. */
  function priceOf(product: Product, channelId?: string | null): number {
    const base = Number(product?.price) || 0
    if (!channelId) return base
    const override = channelPrice.value?.[channelId]?.[product?.productId]
    if (override === undefined || override === null) return base
    const price = Number(override)
    return Number.isFinite(price) ? price : base
  }

  function isSoldout(productId: string): boolean {
    return !!soldout.value?.[productId]
  }

  function setSettings(v: Versioned<SettingsPayload>): void {
    settings.value = v?.data?.list ?? []
    versions.value = { ...versions.value, settings: v?.version }
  }

  function setProducts(v: Versioned<ProductsPayload>): void {
    products.value = v?.data?.list ?? []
    groups.value = v?.data?.computed ?? []
    versions.value = { ...versions.value, products: v?.version }
  }

  function setChannelPrice(v: Versioned<ChannelPriceMap>): void {
    channelPrice.value = v?.data ?? {}
    versions.value = { ...versions.value, channel_price: v?.version }
  }

  function setMenuSet(v: Versioned<ProductAllowMap>): void {
    menuSet.value = v?.data ?? {}
    versions.value = { ...versions.value, menuSet: v?.version }
  }

  function setSoldout(m: SoldoutMap): void {
    soldout.value = m ?? {}
  }

  function reset(): void {
    settings.value = []
    products.value = []
    groups.value = []
    channelPrice.value = {}
    menuSet.value = {}
    soldout.value = {}
    versions.value = {}
    hydrated.value = false
    storeProfileName.value = ''
  }

  return {
    settings, products, groups, channelPrice, menuSet, soldout, versions, hydrated, storeProfileName,
    storeName, zones, menuGroups, allMenuItems, productById,
    priceOf, isSoldout,
    setSettings, setProducts, setChannelPrice, setMenuSet, setSoldout, reset,
  } satisfies CatalogStoreApi
})
